import React, { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type {
  CRB,
  CreateDocumentsRequest,
  DocumentLifecycleValues,
  DocumentUpload,
  DocumentUploadSymlink,
  DueDiligenceDocument,
  LicenseDocumentDataRequest,
  OnboardingDocumentRequirement,
  TemplateResultResponse,
  User,
  USStates
} from '@gcv/shared';
import { RequirementType, TribalNationLicenseType, tribalNationSelectList } from '@gcv/shared';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Box, Divider, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import Rollbar from 'rollbar';

import { ReactComponent as OpenExternalIcon } from 'assets/images/ic-external.svg';
import { USStateOptions } from 'domain/consts';
import { getComplianceStore } from 'stores/ComplianceStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { rollBarAccessToken } from 'third-party-integrations/rollbar';
import type { Column, Row } from 'ui';
import {
  Button,
  Container,
  ContainerItem,
  Dialog,
  InputDate,
  InputFile,
  InputSelect,
  InputText,
  SubTitle,
  Table
} from 'ui';
import { Text, Tooltip } from 'ui/atoms';
import { InputCheckBox, InputSelectSearch, TableContainer } from 'ui/molecules';
import { infoBoxStyles } from 'ui/theme';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { cropInput } from 'util/format.util';
import { uploadOrgDocumentToS3 } from 'util/s3.util';
import { BusinessLicensesWorkflowModals } from './business-licenses-workflow-modals';

interface SharedDocumentsTableData {
  file_name: string;
  uploaded_on: string;
  requirement_name: string;
  document_id: string;
  org_id: string;

  tribal_nation?: boolean;
  tribal_nation_license_type?: TribalNationLicenseType;
  expiration_date?: string;
  license_name?: string;
  license_number?: string;
  state?: USStates;
  lifecycle_values?: DocumentLifecycleValues;
}

interface UploadDocumentPropsV2 {
  addDocumentsToRequirement: (documents: CreateDocumentsRequest) => Promise<void>;
  openDocumentNewWindow?: (docId: string, file_name: string) => Promise<void>;
  toggleModal: (isOpen: boolean) => void;
  modalOpen: boolean;
  requirement: OnboardingDocumentRequirement;
  dispensary: CRB;
  companyType: 'bank' | 'dispensary';
  userMap: User[];
  editDocument?: DueDiligenceDocument;
  sharedRootDocs?: {
    requirement: OnboardingDocumentRequirement;
    ddDocuments: DueDiligenceDocument[];
    documents: DocumentUpload[];
  }[];
  templateResults?: TemplateResultResponse;
  allDocumentsMap?: {
    [id: string]: DocumentUpload;
  };
}

/**
 * Because the form values are nested and contain information for both existing documents and new documents, this const helps
 * us keep track of the form values for the newly uploaded document
 */
const NEW_DOCUMENT_ID = 'new_document';
interface UploadDocumentsFormValues extends FieldValues {
  documents: {
    [NEW_DOCUMENT_ID]: {
      license_data: LicenseDocumentDataRequest & { isTribalNation: boolean };
      lifecycle_values: DocumentLifecycleValues;
    };
    [existingDocId: string]: {
      license_data: LicenseDocumentDataRequest;
      lifecycle_values: DocumentLifecycleValues;
    };
  };
}

export const UploadDocumentModalV2: React.FC<UploadDocumentPropsV2> = (props) => {
  const {
    addDocumentsToRequirement,
    requirement,
    dispensary,
    companyType,
    toggleModal,
    modalOpen,
    editDocument
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { handleSubmit, ...form } = useForm({
    defaultValues: {
      documents: {
        [NEW_DOCUMENT_ID]: {
          license_data: {
            state: dispensary.is_tribal_nation ? '' : dispensary.state,
            isTribalNation: dispensary.is_tribal_nation
          }
        },
        ...(editDocument && {
          [editDocument.id]: {
            license_data: {
              state: editDocument.state,
              license_name: editDocument.license_name,
              license_number: editDocument.license_number,
              expiration_date: editDocument.expiration_date,
              isTribalNation: dispensary.is_tribal_nation,
              tribal_nation: editDocument.tribal_nation,
              tribal_nation_license_type: editDocument.tribal_nation_license_type
            },
            lifecycle_values: editDocument.lifecycle_values
          }
        })
      }
    } as UploadDocumentsFormValues
  } as FieldValues);

  const complianceStore = getComplianceStore();
  const snackbarStore = getSnackbarStore();

  const [rollbar] = useState(
    new Rollbar({
      accessToken: rollBarAccessToken,
      captureUncaught: true,
      captureUnhandledRejections: true
    })
  );

  const [loading, setLoading] = useState(false);

  /**
   * Keeps track of the new files pending upload from the file input box
   */
  const [files, setFiles] = useState<File[]>([]);

  /**
   * CRBM Specific things.
   * uploadedFiles - stores the uploaded file data while we load the additional CRBM modal
   * licenseData - stores the user selected license data when they pick something in CRBM modals
   * openCrbmLicenseSearch - open/close the modal
   *
   */
  const [uploadedFiles, setUploadedFiles] = useState<{ filename: string; s3_key: string }[]>([]);
  const [formData, setFormData] = useState<UploadDocumentsFormValues>({} as UploadDocumentsFormValues);
  const [openCrbmLicenseSearch, setOpenCrbmLicenseSearch] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<{ licenseId?: string; businessId?: string }>();

  /**
   * Used when we show previously uploaded documents to upload to different requirements
   */
  const [selectedDocuments, setSelectedDocuments] = React.useState<SharedDocumentsTableData[]>([]);
  /**
   * Used when the user chooses to upload a new document to a license or lifecycle tracked document
   */
  const [showUploadForm, setShowUploadForm] = useState<boolean>(false);

  const isBusinessLicenseRequirement = requirement.type === RequirementType.BusinessLicense;
  const isLifeCycleRequirement = requirement.lifecycle_config?.lifecycle_enabled;
  const requiresAdditionalFields = isBusinessLicenseRequirement || isLifeCycleRequirement;

  const openFailureSnackbar = () =>
    snackbarStore.showErrorSnackbarMessage('There was an issue uploading one or more of your documents.');

  const onSubmit = async (data: UploadDocumentsFormValues) => {
    if (isBusinessLicenseRequirement) {
      try {
        setLoading(true);
        setFormData({ ...data });

        if (files.length) {
          await uploadDocuments(files).then(async (s3Files) => {
            setUploadedFiles(s3Files);

            //check if some files failed to upload, show error if so
            if (s3Files.length < files.length) {
              openFailureSnackbar();
            }

            setLoading(false);

            if (!form.getValues(`documents.${NEW_DOCUMENT_ID}.license_data.isTribalNation`)) {
              setOpenCrbmLicenseSearch(true);
            } else {
              toggleModal(false);
              await updateFileData(s3Files, data);
              dispensary.is_tribal_nation = true;

              form.reset({
                [`documents.${NEW_DOCUMENT_ID}.license_data.isTribalNation`]: true
              });
            }
          });
        } else {
          await updateFileData(uploadedFiles, data);
        }
      } catch (e) {
        toggleModal(false);
        setOpenCrbmLicenseSearch(false);

        snackbarStore.showErrorSnackbarMessage('There was an issue loading license data.');
      }
    } else {
      setLoading(true);

      const uploadedFiles = await uploadDocuments(files);
      setUploadedFiles(uploadedFiles);

      //check if some files failed to upload, show error if so
      if (uploadedFiles.length < files.length) {
        toggleModal(false);
        setOpenCrbmLicenseSearch(false);
        setLoading(false);
        openFailureSnackbar();
        return;
      }

      toggleModal(false);
      setOpenCrbmLicenseSearch(false);
      setLoading(false);
      await updateFileData(uploadedFiles, data);

      snackbarStore.showSuccessSnackbarMessage('File(s) have been uploaded/added successfully.');
    }
  };

  const uploadDocuments = async (blobs: File[]) => {
    if (!blobs.length) {
      return [];
    }
    return Promise.all(
      blobs.map(async (blob) => {
        const link = await uploadOrgDocumentToS3(blob, dispensary.id);
        return { filename: blob.name, s3_key: link };
      })
    )
      .then((files) => files.filter((f) => f))
      .catch((err) => {
        rollbar.error('0kb file ' + err);
        openFailureSnackbar();
        return [];
      });
  };

  const updateFileData = async (
    files?: { filename: string; s3_key: string }[],
    data?: UploadDocumentsFormValues
  ) => {
    const filesToUse = files ?? uploadedFiles;

    const formDataToUse = { ...formData, ...data };

    const documents: CreateDocumentsRequest['documents'] =
      filesToUse &&
      filesToUse.map((file) => {
        const lifecycle_values = requirement.lifecycle_config
          ? _.merge(
              {},
              requirement.lifecycle_config,
              formDataToUse?.documents[`${NEW_DOCUMENT_ID}`].lifecycle_values
            )
          : undefined;
        const license_data = isBusinessLicenseRequirement
          ? {
              ...formDataToUse?.documents[`${NEW_DOCUMENT_ID}`].license_data,
              crbm_license_id: selectedLicense?.licenseId?.toString(),
              crbm_business_id: selectedLicense?.businessId?.toString()
            }
          : undefined;
        return {
          ...file,
          req_name: requirement.name,
          shared: true,
          lifecycle_values,
          license_data
        };
      });

    if (props.sharedRootDocs && selectedDocuments.length) {
      for (const rootDoc of props.sharedRootDocs) {
        for (const doc of rootDoc.documents) {
          const selectedDoc = selectedDocuments.find((sd) => sd.document_id === doc.id);
          if (selectedDoc) {
            const lifecycle_values = requirement.lifecycle_config
              ? _.merge(
                  {},
                  requirement.lifecycle_config,
                  formDataToUse?.documents[`${doc.id}`].lifecycle_values
                )
              : undefined;
            const license_data = isBusinessLicenseRequirement
              ? {
                  ...formDataToUse?.documents[`${doc.id}`].license_data
                }
              : undefined;
            documents.push({
              filename: doc.file_name,
              req_name: requirement.name,
              s3_key: doc.s3_key,
              linked_doc_id: `${doc.org_id}_${doc.id}`,
              lifecycle_values,
              license_data
            });
          }
        }
      }
    }

    const body: CreateDocumentsRequest = {
      documents,
      license_data: editDocument ? formDataToUse?.documents[`${editDocument.id}`].license_data : undefined
    };

    await addDocumentsToRequirement(body)
      .then(() => {
        toggleModal(false);
        setOpenCrbmLicenseSearch(false);
        setLoading(false);
        setFiles([]);
        setSelectedDocuments([]);
      })
      .catch(() => {
        openFailureSnackbar();
        setLoading(false);
        setFiles([]);
        setSelectedDocuments([]);
      });

    setSelectedDocuments([]);
  };

  const sharedDocumentsColumns: Column<SharedDocumentsTableData>[] = [
    {
      id: 'file_name',
      label: 'My Documents',
      widthPercent: !isMobile ? 45 : undefined,
      format: (td) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <div>{isMobile ? cropInput(td.file_name, 12, 'document') : td.file_name}</div>
            <ContainerItem padding={0} margin={0}>
              <div
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  if (props.openDocumentNewWindow) {
                    props.openDocumentNewWindow(td.document_id, td.file_name);
                  }
                }}
              >
                <OpenExternalIcon />
              </div>
            </ContainerItem>
          </div>
        );
      }
    },
    { id: 'requirement_name', label: 'Requirement', widthPercent: !isMobile ? 35 : undefined },
    {
      id: 'uploaded_on',
      label: 'Uploaded On',
      widthPercent: !isMobile ? 20 : undefined,
      format: (row) =>
        DateTimeHelpers.formatISOToTableDateString(row.uploaded_on ?? '', dispensary.iana_timezone)
    }
  ];

  const sharedDocumentsRows: Row<SharedDocumentsTableData>[] = [];

  const isRootOrLinkedInRequirementResult = (sharedRootDoc: DocumentUpload): boolean => {
    if (!props.templateResults || !props.allDocumentsMap) return false;
    const requirementResult = props.templateResults.requirements_results.find(
      (docReqResult) => docReqResult.requirement_id === requirement.requirement_id
    );
    if (!requirementResult?.documents || Object.keys(requirementResult.documents).length === 0) {
      return false;
    } else {
      const requirementResultDocIds = Object.keys(requirementResult.documents);
      if (requirementResultDocIds.includes(sharedRootDoc.id)) {
        return true;
      }
      for (const requirementResultDocId of requirementResultDocIds) {
        const linkedDocId = (props.allDocumentsMap[requirementResultDocId] as DocumentUploadSymlink)
          .linked_doc_id;
        if (!linkedDocId) continue;
        const splitLinkedDocId = linkedDocId.split('_');
        if (splitLinkedDocId.length !== 2) continue;
        const rootDocId = splitLinkedDocId[1];
        if (sharedRootDoc.id === rootDocId) {
          return true;
        }
      }
      return false;
    }
  };

  if (props.sharedRootDocs && props.modalOpen) {
    props.sharedRootDocs.map((sharedRootDoc) => {
      if (
        sharedRootDoc.requirement.requirement_id === requirement.requirement_id ||
        !isBusinessLicenseRequirement
      ) {
        for (const document of sharedRootDoc.documents) {
          if (!document.deleted && !isRootOrLinkedInRequirementResult(document)) {
            const ddDocument = sharedRootDoc.ddDocuments.find((d) => d.id === document.id);
            sharedDocumentsRows.push({
              id: document.id,
              data: {
                file_name: document.file_name,
                uploaded_on: document.upload_date,
                requirement_name: sharedRootDoc.requirement.name,
                document_id: document.id,
                expiration_date: ddDocument?.expiration_date,
                license_name: ddDocument?.license_name,
                license_number: ddDocument?.license_number,
                state: ddDocument?.state,
                lifecycle_values: ddDocument?.lifecycle_values,
                tribal_nation: ddDocument?.tribal_nation,
                tribal_nation_license_type: ddDocument?.tribal_nation_license_type
              }
            } as Row<SharedDocumentsTableData>);
          }
        }
      }
    });
  }

  const getInitiallySelectedDocIds = (): string[] => {
    const docIds: string[] = [];
    if (props.sharedRootDocs) {
      props.sharedRootDocs.map((doc) => {
        if (doc.requirement.requirement_id === requirement.requirement_id) {
          const docsInRows = doc.documents.filter((d) =>
            sharedDocumentsRows.find((r) => r.data.document_id === d.id)
          );
          docIds.push(...docsInRows.map((d) => d.id));
        }
      });
    }
    return docIds;
  };

  const lifeCycleTrackingFields = (existingDocumentId?: string) => {
    const documentId = existingDocumentId ?? NEW_DOCUMENT_ID;
    const background = existingDocumentId && !editDocument ? 'white' : undefined;

    // check to see if the lifecycle doc has mismatched select values and clear the form if so
    if (existingDocumentId && requirement.lifecycle_config?.select?.enabled) {
      const existingDoc = selectedDocuments.find((d) => d.document_id === existingDocumentId);
      if (
        existingDoc?.lifecycle_values?.select?.value &&
        !requirement.lifecycle_config?.select.select_options
          ?.split(';')
          .includes(existingDoc.lifecycle_values.select.value)
      ) {
        form.setValue(`documents.${documentId}.lifecycle_values.select.value`, undefined);
      }
    }

    return (
      <>
        {requirement.lifecycle_config?.expiration_date?.enabled && (
          <ContainerItem padding="8px 0" width="100%">
            <InputDate
              {...form}
              name={`documents.${documentId}.lifecycle_values.expiration_date.value`}
              rules={{ required: { message: 'is required', value: true } }}
              label={requirement.lifecycle_config.expiration_date.display_label}
              timezone={dispensary.iana_timezone}
              dataCy="lifecycle-expiration"
              background={background}
            />
          </ContainerItem>
        )}
        {requirement.lifecycle_config?.identifier?.enabled && (
          <ContainerItem padding="8px 0" width="100%">
            <InputText
              {...form}
              name={`documents.${documentId}.lifecycle_values.identifier.value`}
              rules={{ required: { message: 'is required', value: true } }}
              label={requirement.lifecycle_config.identifier.display_label}
              dataCy="lifecycle-identifier"
              background={background}
            />
          </ContainerItem>
        )}
        {requirement.lifecycle_config?.text?.enabled && (
          <ContainerItem padding="8px 0" width="100%">
            <InputText
              {...form}
              name={`documents.${documentId}.lifecycle_values.text.value`}
              rules={{ required: { message: 'is required', value: true } }}
              label={requirement.lifecycle_config.text.display_label}
              dataCy="lifecycle-text"
              background={background}
            />
          </ContainerItem>
        )}
        {requirement.lifecycle_config?.select?.enabled && (
          <ContainerItem padding="8px 0" width="100%">
            <InputSelect
              {...form}
              name={`documents.${documentId}.lifecycle_values.select.value`}
              rules={{ required: { message: 'is required', value: true } }}
              label={requirement.lifecycle_config.select.display_label}
              options={(requirement.lifecycle_config.select.select_options ?? '').split(';').map((option) => {
                return { label: option, value: option };
              })}
              dataCy="lifecycle-select"
              background={background}
            />
          </ContainerItem>
        )}
      </>
    );
  };

  const licenseFields = (existingDocumentId?: string) => {
    const documentId = existingDocumentId ?? NEW_DOCUMENT_ID;
    const background = existingDocumentId && !editDocument ? 'white' : undefined;
    return (
      <>
        <ContainerItem padding="8px 0" width="100%">
          <Box sx={{ mb: '1rem' }}>
            <Tooltip
              title={
                form.watch(`documents.${documentId}.license_data.isTribalNation`)
                  ? 'To select a state, uncheck the Tribal Nation checkbox.'
                  : ''
              }
            >
              <InputSelect
                {...form}
                name={`documents.${documentId}.license_data.state`}
                label={'State'}
                options={USStateOptions}
                rules={{
                  required: {
                    message: 'is required',
                    value: form.watch(`documents.${documentId}.license_data.isTribalNation`) ? false : true
                  }
                }}
                dataCy="license-state"
                placeholder="Select State"
                readOnly={form.watch(`documents.${documentId}.license_data.isTribalNation`)}
                background={background}
              />
            </Tooltip>
          </Box>

          <Box sx={{ mb: '1rem' }}>
            <InputCheckBox
              {...form}
              defaultValue={dispensary.is_tribal_nation}
              label={'Tribal nation license holder? Check this box.'}
              name={`documents.${documentId}.license_data.isTribalNation`}
              readOnly={dispensary.is_tribal_nation}
              onChange={() => {
                form.setValue(`documents.${documentId}.license_data.state`, '');
              }}
            />
          </Box>
          <>
            {form.watch(`documents.${documentId}.license_data.isTribalNation`) && (
              <>
                <Box sx={{ mb: '1rem' }}>
                  <InputSelectSearch
                    {...form}
                    options={tribalNationSelectList()}
                    label={'Tribal Nation'}
                    name={`documents.${documentId}.license_data.tribal_nation`}
                    rules={{ required: { message: 'is required', value: true } }}
                    dataCy="tribal_nation"
                    placeholder="Search"
                    background={background}
                  />
                </Box>
                <Box sx={{ mb: '1rem' }}>
                  <InputSelect
                    {...form}
                    options={Object.entries(TribalNationLicenseType).map(([key, value]) => {
                      return { label: value, value: key };
                    })}
                    label={'Tribal Nation License Type'}
                    name={`documents.${documentId}.license_data.tribal_nation_license_type`}
                    rules={{ required: { message: 'is required', value: true } }}
                    dataCy="tribal_nation_license_type"
                    placeholder="Select Tribal Nation License Type"
                    background={background}
                  />
                </Box>
              </>
            )}
          </>

          {!form.watch(`documents.${documentId}.license_data.isTribalNation`) && (
            <InputSelect
              {...form}
              options={
                complianceStore.licenseDataMap[
                  form.watch(`documents.${documentId}.license_data.state`) as USStates
                ]?.business_license_types.map((business_license_type) => {
                  return { label: business_license_type.viewValue, value: business_license_type.value };
                }) ?? []
              }
              label={'License Name'}
              name={`documents.${documentId}.license_data.license_name`}
              rules={{ required: { message: 'is required', value: true } }}
              dataCy="license-name"
              background={background}
            />
          )}
        </ContainerItem>

        <ContainerItem padding="8px 0" width="100%">
          <InputText
            {...form}
            name={`documents.${documentId}.license_data.license_number`}
            rules={{ required: { message: 'is required', value: true } }}
            label={'License Number'}
            dataCy="license-number"
            background={background}
          />
        </ContainerItem>

        <ContainerItem padding="8px 0" width="100%">
          <InputDate
            {...form}
            name={`documents.${documentId}.license_data.expiration_date`}
            rules={{ required: { message: 'is required', value: true } }}
            label={'Expiration Date'}
            timezone={dispensary.iana_timezone}
            dataCy="license-expiration"
            background={background}
          />
        </ContainerItem>
      </>
    );
  };

  return (
    <>
      <Dialog
        handleClose={() => {
          toggleModal(false);
          setOpenCrbmLicenseSearch(false);
          setShowUploadForm(false);
          setSelectedDocuments([]);
        }}
        isOpen={modalOpen && !openCrbmLicenseSearch}
        secondAction={
          !showUploadForm && requiresAdditionalFields && sharedDocumentsRows.length > 0 ? (
            <Button
              label={selectedDocuments.length ? 'Save and Upload New' : 'Upload New'}
              onClick={() => {
                setShowUploadForm(true);
              }}
              color="primary"
            />
          ) : undefined
        }
        action={
          <Button
            color="primary"
            label={
              editDocument ? 'Update' : showUploadForm || sharedDocumentsRows.length === 0 ? 'Upload' : 'Save'
            }
            isLoading={loading}
            onClick={handleSubmit((data) => onSubmit(data as UploadDocumentsFormValues))}
            disabled={(!editDocument && selectedDocuments.length === 0 && files.length < 1) || loading}
            width={isMobile ? '100%' : 'fit-content'}
            dataCy="upload-button"
          />
        }
        title={
          editDocument
            ? `Update ${requirement.name}`
            : companyType === 'dispensary' &&
              sharedDocumentsRows.length > 0 &&
              (!showUploadForm || !requiresAdditionalFields)
            ? `Select or upload ${requirement.name}`
            : `Upload ${requirement.name}`
        }
        dataCy="upload-document-modal"
        width={
          isMobile || (companyType === 'dispensary' && sharedDocumentsRows.length > 0 && !showUploadForm)
            ? '100%'
            : undefined
        }
        maxHeight="750px"
      >
        <Container column data-cy="upload-container" margin="0" padding="0">
          {companyType === 'bank' && (
            <ContainerItem justify="center" width="100%" margin="0" padding="0">
              <SubTitle content="The business will see files uploaded here in their Company Profile" />
            </ContainerItem>
          )}
          {companyType === 'dispensary' && sharedDocumentsRows.length > 0 && !showUploadForm && (
            <ContainerItem width="100%" margin="0" padding="0">
              <SubTitle content={`Choose from previously uploaded documents, or upload new documents.`} />
            </ContainerItem>
          )}
          <ContainerItem padding="0 0 8px 0" width="100%">
            <Container padding="0" width="100%">
              {companyType === 'dispensary' &&
                sharedDocumentsRows.length > 0 &&
                (!showUploadForm || !requiresAdditionalFields) && (
                  <ContainerItem padding="8px 0 8px 0" width={isMobile ? '100%' : '60%'}>
                    <TableContainer>
                      <Table
                        selectable
                        columns={sharedDocumentsColumns}
                        rows={sharedDocumentsRows}
                        maxHeight="360px"
                        defaultSort={'selected' as keyof SharedDocumentsTableData}
                        onClick={() => {
                          return;
                        }}
                        returnSelection={(selectedRows) => {
                          const selectedDocs: SharedDocumentsTableData[] = [];
                          for (const row of sharedDocumentsRows) {
                            if (selectedRows.includes(row.id)) {
                              selectedDocs.push(row.data);
                            }
                          }

                          const newDocuments = selectedDocs
                            .filter(
                              (r) => !selectedDocuments.map((d) => d.document_id).includes(r.document_id)
                            )
                            .map((doc) => {
                              return sharedDocumentsRows.find((d) => d.data.document_id === doc.document_id)
                                ?.data;
                            }) as SharedDocumentsTableData[];
                          setSelectedDocuments(selectedDocs);

                          newDocuments.forEach((doc) => {
                            form.setValue(`documents.${doc.document_id}.license_data.state`, doc.state);
                            form.setValue(
                              `documents.${doc.document_id}.license_data.license_name`,
                              doc.license_name
                            );
                            form.setValue(
                              `documents.${doc.document_id}.license_data.license_number`,
                              doc.license_number
                            );
                            form.setValue(
                              `documents.${doc.document_id}.license_data.expiration_date`,
                              doc.expiration_date
                            );
                            form.setValue(
                              `documents.${doc.document_id}.license_data.tribal_nation`,
                              doc.tribal_nation
                            );
                            form.setValue(
                              `documents.${doc.document_id}.license_data.tribal_nation_license_type`,
                              doc.tribal_nation_license_type
                            );
                            form.setValue(
                              `documents.${doc.document_id}.lifecycle_values.expiration_date.value`,
                              doc.lifecycle_values?.expiration_date?.value
                            );
                            form.setValue(
                              `documents.${doc.document_id}.lifecycle_values.text.value`,
                              doc.lifecycle_values?.text?.value
                            );
                            form.setValue(
                              `documents.${doc.document_id}.lifecycle_values.identifier.value`,
                              doc.lifecycle_values?.identifier?.value
                            );
                            form.setValue(
                              `documents.${doc.document_id}.lifecycle_values.select.value`,
                              doc.lifecycle_values?.select?.value
                            );
                          });
                        }}
                        defaultSelected={
                          props.sharedRootDocs &&
                          props.sharedRootDocs.some(
                            (srd) => srd.requirement.requirement_id === requirement.requirement_id
                          )
                            ? getInitiallySelectedDocIds()
                            : undefined
                        }
                        minWidth={isMobile ? '600px' : undefined}
                        noPagination
                      />
                    </TableContainer>
                  </ContainerItem>
                )}

              <ContainerItem
                width={
                  isMobile
                    ? '100%'
                    : companyType === 'dispensary' && sharedDocumentsRows.length > 0 && !showUploadForm
                    ? '40%'
                    : '100%'
                }
              >
                <Box
                  sx={{
                    borderLeft:
                      !isMobile &&
                      companyType === 'dispensary' &&
                      sharedDocumentsRows.length > 0 &&
                      !showUploadForm
                        ? `1px solid ${palette.border}`
                        : undefined,
                    minHeight:
                      !isMobile &&
                      companyType === 'dispensary' &&
                      sharedDocumentsRows.length > 0 &&
                      !showUploadForm
                        ? '400px'
                        : undefined,
                    paddingLeft:
                      !isMobile &&
                      companyType === 'dispensary' &&
                      sharedDocumentsRows.length > 0 &&
                      !showUploadForm
                        ? '8px'
                        : undefined
                  }}
                >
                  {companyType === 'dispensary' && sharedDocumentsRows.length > 0 && !showUploadForm && (
                    <Box sx={{ width: '100%' }}>
                      <Text content="Selected documents" />

                      {selectedDocuments.length === 0 && (
                        <Text
                          type="body2"
                          sx={{ color: palette.text.secondary }}
                          content="There are no documents selected"
                        />
                      )}
                      {selectedDocuments.map((doc) => {
                        return (
                          <Box
                            key={doc.document_id}
                            sx={{
                              ...infoBoxStyles,
                              flexDirection: 'column',
                              gap: 1,
                              backgroundColor: palette.background.default,
                              width: '100%'
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <DescriptionOutlinedIcon style={{ color: palette.primary.main }} />
                              <Text type="body2" sx={{ fontWeight: '700' }} content={doc.file_name} />
                            </Box>
                            {isLifeCycleRequirement && (
                              <>
                                <Divider />
                                {lifeCycleTrackingFields(doc.document_id)}
                              </>
                            )}
                            {isBusinessLicenseRequirement && (
                              <>
                                <Divider />
                                {licenseFields(doc.document_id)}
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {(sharedDocumentsRows.length === 0 || showUploadForm) && isBusinessLicenseRequirement
                    ? licenseFields(editDocument ? editDocument.id : undefined)
                    : null}
                  {(sharedDocumentsRows.length === 0 || showUploadForm) && isLifeCycleRequirement
                    ? lifeCycleTrackingFields(editDocument ? editDocument.id : undefined)
                    : null}
                  {!editDocument &&
                    (sharedDocumentsRows.length === 0 || showUploadForm || !requiresAdditionalFields) && (
                      <ContainerItem width="100%" margin="1rem 0 0 0" padding="8px 0">
                        <Text content="Upload a new document" />
                        <InputFile
                          fullWidth
                          onFilesChange={(allFiles) => setFiles(allFiles)}
                          acceptedTypes={'all'}
                          multiple={!requiresAdditionalFields}
                          dataCy="upload-file-container"
                        />
                      </ContainerItem>
                    )}
                </Box>
              </ContainerItem>
            </Container>
          </ContainerItem>
        </Container>
      </Dialog>

      {openCrbmLicenseSearch && formData?.documents?.[`${NEW_DOCUMENT_ID}`]?.license_data?.license_number && (
        <BusinessLicensesWorkflowModals
          dispensary={dispensary}
          resetModal={() => {
            setOpenCrbmLicenseSearch(false);
            toggleModal(true);
          }}
          updateFileData={updateFileData}
          setSelectedLicense={setSelectedLicense}
          licenseNumber={formData.documents?.[`${NEW_DOCUMENT_ID}`]?.license_data?.license_number}
        />
      )}
    </>
  );
};
